/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import axios from 'axios';
import { Component, FormEvent, ReactElement } from 'react';
import { Alert, Modal, Radio, message } from 'antd';
import { withRouter } from 'react-router';
import FormTitle from '../../_shared/form-title';
import SharedStyles from '../../_shared/styles';
import { Button } from '../../_shared/button';
import styles from './manufacture-process.styles';
import {
  getEquipments,
  createOrUpdateEquipment,
  deleteEquipment,
  getEquipmentRecord,
  recordFailedTest,
} from '../../_shared/services/manage-equipment.service';
import { _Equipment, Equipment } from '../../_shared/interfaces/equipment';
import moment from 'moment';
import {
  findModel,
  uppercaseFirst,
  buildErrorMsgFromForm,
  defaultDateFormat,
  sortByValue,
  downloadFile,
  isHandpiece,
  isNonSmartChair,
  _checkEquipmentSN,
  isPrototypeSerial,
  normalizeSerialNumber,
  getEqType,
  motorControlBoardTests,
  isBetween,
  ensureArray,
} from '../../utils';
import { validateSerialField } from '../../_shared/serial-number-input';
import { debounce, get, set, size, first, orderBy } from 'lodash';
import { Model } from '../../_shared/interfaces/model';
import colors from '../../_shared/colors';
import { sendDeviceCommand } from '../../_shared/services/manage-schedule.service';
import { TableList } from '../../_shared/table-list';
import { getSerial } from '../../_shared/services/manage-serials.service';
import { getModels } from '../../_shared/services/manage-models.service';
import { IFormItem, IProps, IState } from './manufacture-process.interfaces';
import {
  checkEquipForController,
  checkEquipHasDifferentController,
  getEquipWithControllerandEquipSN,
  generateTestCommand,
  checkContAssocWithOtherEquip,
  getAllEquipmentWithEquipSN,
  updateParticleName,
} from './manufacture-process.utils';
import CONFIG, { ENVS, IAppConfig, isProd } from '../../config';
import Bugsnag from '@bugsnag/js';
import { AIcon as Icon } from '../../_shared/icon';
import { WithForm } from '../../_shared/form';
import { WithAppConfig } from '../../_shared/hooks/useAppConfig';
import { Tutorial } from '../../_shared/tutorial';
import { checkForNewStatus } from '../dashboard.actions';
import { createPublish } from '../../_shared/services/publish.service';

const generateEquipColumns = (_this: _ManufactureProcess) => {
  const cols = [
    {
      title: 'Type',
      dataIndex: 'type',
      width: '10%',
      render: (_text: string, record: Equipment) => {
        const model = findModel(record, _this.props.sState);
        return <span>{uppercaseFirst(get(model, 'type', ''))}</span>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: '8.5%',
      render: (_text: string, record: Equipment) => {
        const testResults = get(record, 'status.test');
        const isCompleted = !!record.testCompletion;
        const passedTests = get(testResults, 'overall');
        const status =
          isCompleted && passedTests
            ? 'Passed'
            : isCompleted && !passedTests
              ? 'Failed'
              : 'In Progress';
        return (
          <span
            css={css(
              isCompleted && !passedTests && styles.failed,
              isCompleted && passedTests && styles.passed
            )}
          >
            {status}
          </span>
        );
      },
    },
    {
      title: 'Equipment SN',
      dataIndex: 'equipmentSN',
      width: '11%',
    },
    {
      title: 'Show on Dash',
      dataIndex: 'showOnDash',
      width: '8.5%',
      render: (_text: string, record: Equipment) => {
        const showOnDash = isNonSmartChair(record.modelId) ? false : record.showOnDash;
        return (
          <span css={css(showOnDash ? styles.passed : styles.failed)}>
            {showOnDash ? 'True' : 'False'}
          </span>
        );
      },
    },
    {
      title: 'Command Line',
      render: (_text: string, record: Equipment) => {
        if (isNonSmartChair(record.modelId) || !record.equipmentSN || !(record.equipmentSN.startsWith('X'))) {
          return null;
        }

       return <Icon
          onClick={() => _this.showCommandLineModal(record)}
          style={{
            borderRadius: '0px 5px 0px 5px',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '16px',
          }}
          type='code'
          />;
      },
    },
    {
      title: 'Active Alerts',
      width: '8.5%',
      dataIndex: '',
      render: (_text: string, record: Equipment) => {
        if (isNonSmartChair(record.modelId)) {
          return '';
        }
        const installDate = moment(get(record, 'installDate'));
        // AER-1749: honour the model's ignoreAlerts here too, so suppressed
        // sources stop inflating the manufacturing alert count. status.alerts
        // carries `source` at the top level (AlertStatus), not under `data`.
        const ignore = get(
          findModel(record, _this.props.sState),
          'ignoreAlerts',
          []
        ) as string[];
        const alerts = (get(record, 'status.alerts', []) as string[]).filter(a => {
          const time = moment((get(a, 'time') as any) * 1000);
          const asource = get(a, 'source') as any;
          return time.isAfter(installDate) && ignore.indexOf(asource) === -1;
        });
        const alertCount = size(alerts);
        return (
          <span css={css(styles.failed)}>
            {alertCount && alertCount > 1
              ? `${alertCount} Alerts`
              : alertCount && alertCount === 1
                ? `${alertCount} Alert`
                : ''}
          </span>
        );
      },
    },
    {
      title: 'Controller SN',
      dataIndex: 'controllerSerial',
      width: '15%',
    },
    {
      title: 'Test Start',
      dataIndex: 'test',
      width: '15%',
      render: (_text: string, record: Equipment) => {
        const time = record.test
          ? moment(record.test).format(defaultDateFormat)
          : '';
        return <span>{time}</span>;
      },
    },
    {
      title: 'Test Completion',
      dataIndex: 'testCompletion',
      width: '15%',
      render: (_text: string, record: Equipment) => {
        const time = moment(record.testCompletion).format(defaultDateFormat);
        return <span>{record.testCompletion ? time : ''}</span>;
      },
    },
    {
      title: 'Test Results',
      dataIndex: 'testDataUrl',
      width: '8.5%',
      render: (url: string) => {
        return url ? (
          <button
            css={css(styles.link)}
            onClick={async () => await downloadFile(url)}
          >
            Test Results
          </button>
        ) : null;
      },
    },
  ];

  return cols;
};

export class _ManufactureProcess extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    const equipment = new _Equipment();

    const blurCheck = async () => {
      const {
        form: { getFieldValue },
      } = this.props;
      const allEquipment = this.state.allEquipments;
      const str1 = this.getFieldKey('controllerSerial');
      const that1 = get(this, str1);
      const controllerSerialInput = getFieldValue(that1.var)
        .trim()
        .toUpperCase();

      const ignore = this.ignoreValidation();

      const str2 = this.getFieldKey('equipmentSN');
      const that2 = get(this, str2);
      const equipmentSNInput = getFieldValue(that2.var)
        .trim()
        .toUpperCase();
      const equipWithSN = getAllEquipmentWithEquipSN(
        equipmentSNInput,
        allEquipment
      );

      const controllerAssocWithEquip = checkEquipForController(
        controllerSerialInput,
        allEquipment
      );

      const equipWithESNandECS = getEquipWithControllerandEquipSN(
        equipmentSNInput,
        controllerSerialInput,
        allEquipment
      );

      const equipHasOtherController = checkEquipHasDifferentController(
        controllerSerialInput,
        equipmentSNInput,
        allEquipment
      );

      if (!controllerSerialInput || !equipmentSNInput || ignore) return;

      // AER-1676: the reused Particle board on non-smart chairs (5400/CRK) is
      // intentionally shared across units, so suppress the "controller already
      // associated with another unit" warnings — but keep the equipmentSN-duplicate
      // warning (scenario 4) below.
      const nonSmart = isNonSmartChair(getFieldValue('modelId'));

      //scenario 4
      if (
        !controllerAssocWithEquip &&
        !!size(equipWithSN) &&
        equipHasOtherController
      ) {
        return await this.openEquipHasDiffControllerModal(
          equipWithSN,
          controllerSerialInput
        );
      }

      if (nonSmart) return;

      //scenario 5
      if (!!equipWithESNandECS) {
        return await this.openEquipExistsModal(
          controllerSerialInput,
          equipWithESNandECS
        );
      }
      //scenario 2 and 3

      this.checkControllerSerialNumber(
        controllerSerialInput,
        equipmentSNInput,
        equipWithSN
      );
    };

    // AER-1795: runs once the equipment SN has been checked (on blur, or again after a
    // model change). A valid SN continues into the non-connected-unit confirmation and the
    // controller/equipment association checks, as the old onBlur handler did.
    const afterSerialValidated = async (serial: string, errors: string[]) => {
      if (size(errors)) {
        return;
      }
      // If SN begins with X, confirm before proceeding
      if (serial.startsWith('X')) {
        const confirmed = await new Promise<Boolean>(resolve => {
          Modal.confirm({
            title: 'Confirmation',
            content:
              'Are you sure that this is a non-connected unit? It will be necessary to manually update the firmware as part of the manufacturing process. This update can not be undone.',
            okText: 'Confirm',
            cancelText: 'Reset',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          })
        })

        if (!confirmed) {
          this.props.form.setFieldsValue({ equipmentSN: '' });
        }
      }
      blurCheck();
    };

    const revalidateSerial = async () => {
      const {
        form: { getFieldValue },
      } = this.props;
      const errors = await validateSerialField(this.props.form, 'equipmentSN');
      if (errors) {
        await afterSerialValidated(normalizeSerialNumber(getFieldValue('equipmentSN')), errors);
      }
    };

    const setFieldError = (
      key: string,
      errors: undefined | Error[] = undefined
    ) => {
      const {
        form: { setFields, getFieldValue, getFieldError },
      } = this.props;

      const value = getFieldValue(key);
      const str = this.getFieldKey(key);

      const hasErrors = !!size(errors);

      const ico = hasErrors ? (
        <Icon type={'close-circle'} css={css(`color: ${colors.error};`)} />
      ) : (
        <Icon type={'check-circle'} css={css(`color: ${colors.success};`)} />
      );

      set(this, str + '.props.suffix', ico);

      setFields([
        {
          name: key,
          value,
          errors: size(errors) ? (errors as Error[]).map(e => e.message) : [],
        },
      ]);

      // AER-1657: setFieldError mutates props.suffix in place but, unlike the
      // controller-serial onBlur, never re-rendered — so the equipment-SN ✓/✗
      // only painted when some later setState happened to flush it. Flush here so
      // the check mark reliably appears as soon as the check resolves. Safe for
      // focus: setFieldError runs deferred (post-blur), so toggling the suffix
      // can't steal focus from the input.
      this.setState({});
    };

    const generateDisplay2 = (modelId?: string) => {
      const models = get(this.props.sState, 'dash.models', []) as Model[];
      const model = models.find(m => m.id === modelId);
      const mHeads = get(model, 'heads', 1);
      // AER-1675: the model-id-prefix heuristic treats anything starting with 'C'
      // as a compressor, which wrongly matched the Chair Replacement Kit ('CRK')
      // and rendered motor-head / tank / dryer serial fields on the CRK form.
      // Chairs are never compressors, so exclude them explicitly.
      const eqType = this.getEqType();
      const isCompressor = !!modelId && modelId[0] === 'C' && eqType !== 'chair' && !isNonSmartChair(modelId);

      const toDisplay2 = [];

      if (!modelId) {
        //do nothing
      } else if (isCompressor) {
        for (let i = 0; i < mHeads; i++) {
          const ii = i + 1;
          toDisplay2.push({
            title: `Motor Head ${ii} Serial Number`,
            var: `mSerial${ii}`,
            options: {
              rules: [
                {
                  required: true,
                  message: `Motor Head ${ii} is required`,
                },
              ],
            },
            subtext: () =>
              'REMINDER: motor heads should be scanned in order from left to right',
          });
        }

        toDisplay2.push({
          title: `Tank Serial Number`,
          var: `tankSerial`,
          options: {
            rules: [
              {
                required: true,
                message: `Tank Serial Number is required`,
              },
            ],
          },
        });

        toDisplay2.push({
          title: `Dryer Serial Number`,
          var: `dryerSerial`,
          options: {
            rules: [
              {
                required: true,
                message: `Dryer Serial Number is required`,
              },
            ],
          },
        });
      } else if (eqType === 'handpiece') {
        toDisplay2.push({
          title: `Motor Control Board`,
          var: `mSerial_motorboard`,
          options: {
            rules: [
              {
                required: true,
                message: `Motor Control Board is required`,
              },
            ],
          },
          props: {
            onBlur: async () => {
              const {
                form: { setFields, getFieldValue, getFieldError },
              } = this.props;

              const key = 'mSerial_motorboard';
              let value = getFieldValue(key);

              if (!value) return;

              const ignore = this.ignoreValidation();
              const isValid = motorControlBoardTests(value.toUpperCase());

              const setTheFields = (errs: undefined | Error[] = undefined) => {
                setTimeout(() => setFieldError('mSerial_motorboard', errs), 0);
              };

              if (ignore || !size(isValid)) {
                setTheFields(undefined);
                // blurCheck();
              } else {
                setTheFields([first(isValid) as Error]);
              }
            },
          },
        });

        toDisplay2.push({
          title: `Lock Presets`,
          var: `lockPresets`,
          type: 'checkbox',
        });
      } else if (eqType == 'chair') {
        //nothing yet
      } else {
        toDisplay2.push({
          title: `Motor Serial Number`,
          var: `mSerial1`,
          options: {
            rules: [
              {
                required: true,
                message: `Motor SN is required`,
              },
            ],
          },
        });

        toDisplay2.push({
          title: `Vacuum Pump Serial Number`,
          var: `pumpSerial`,
          options: {
            rules: [
              {
                required: true,
                message: `Vacuum Pump Serial Number is required`,
              },
            ],
          },
        });
      }

      this.setState({
        toDisplay2: (toDisplay2 as unknown) as IFormItem[],
      });
    };

    const userEqOptions: any = get(props, 'sState.auth.user.eqTypeOptions', [
      'utility',
    ]);
    const hasOneOption = size(userEqOptions) === 1;

    const toDisplay = ([
      {
        title: 'Equipment Type',
        var: 'eqType',
        type: 'dropdown',
        opts: () =>
          [
            { label: 'Utility', value: 'utility' },
            { label: 'Handpiece', value: 'handpiece' },
            { label: 'Equipment', value: 'chair' },
          ].filter(f => userEqOptions.indexOf(f.value) > -1),
        props: {
          initialValue: hasOneOption ? first(userEqOptions) : 'utility',
          disabled: size(userEqOptions) <= 1,
          onChange: (v: string) => {
            const {
              form: { setFields, getFieldValue },
            } = this.props;
            // AER-1646: `setFieldsOriginal()` with no args recomputes every field
            // from state.equipment / props.initialValue. _Equipment has no `eqType`,
            // so eqType falls back to its initialValue ('utility' for any multi-option
            // user) and the dropdown snaps straight back to Utility the instant the
            // user picks Handpiece/Equipment. Pass the newly-selected value through
            // (same pattern as clear()) so the selection sticks and the dependent
            // fields + Recently Tested grid refresh for the chosen type.
            this.setFieldsOriginal({ eqType: v });
            // AER-1734: the serial field is a single Form.Item for all three types
            // (`var: 'equipmentSN'` below) — only its label and its `required` message
            // are derived from eqType — so it never unmounts on a type change and
            // rc-field-form keeps the error string it was last given, verbatim. Blur
            // the empty field as Equipment, then switch to Utility, and "Chair Serial
            // Number is required" stays sitting under a field now labelled "Equipment
            // Serial Number". setFieldsOriginal only writes values, and setFieldsValue
            // neither re-validates nor clears errors, so drop the error here; the field
            // re-validates with the right wording on its next blur or on submit. Kept
            // in this handler rather than in setFieldsOriginal, which is shared with
            // the mount and clear paths where there is no stale message to clear.
            setFields([
              {
                name: 'equipmentSN',
                value: getFieldValue('equipmentSN'),
                errors: [],
              },
            ]);
            this.retrieveEquipments();
            generateDisplay2();
          },
        },
      },
      {
        title: 'Equipment Model',
        var: 'modelId',
        type: 'dropdown',
        opts: () => {
          const {
            form: { getFieldValue, setFieldsValue },
          } = this.props;

          const retOpts = this.getSelectableModels(this.ignoreValidation())
            .map((o: Model) => ({
              ...o,
              label: `${o.name} (${o.id})`,
              value: o.id,
            }))
            .sort(sortByValue) as Model[];

          if (size(retOpts) === 1) {
            setTimeout(() => {
              if (!getFieldValue('modelId')) {
                const modelId: any = get(retOpts, '0.value');
                setFieldsValue({ modelId });
                generateDisplay2(modelId);
              }
            }, 0);
          }

          return retOpts;
        },
        props: {
          onChange: (modelId: string) => {
            generateDisplay2(modelId);
            // AER-1683: the equipment SN is validated against the selected model.
            // Re-run that check when the model changes so an already-entered SN
            // is re-evaluated against the new model.
            if (this.props.form.getFieldValue('equipmentSN')) {
              // Defer so the newly-selected modelId is committed to the form
              // before the SN rule reads it.
              setTimeout(() => revalidateSerial(), 0);
            }
          },
        },
        options: {
          rules: [
            {
              required: true,
              message: 'Equipment Model is required.',
            },
          ],
        },
      },
      {
        title: 'Includes SS',
        var: 'include_ss',
        type: 'checkbox',
        canDisplay: () => {
          const {
            form: { getFieldValue },
          } = this.props;
          const validvalues = get(this.props, 'app.ss_values', []) as any[];
          const modelVal = getFieldValue('modelId');
          return validvalues.indexOf(modelVal) > -1;
        },
      },
      {
        title: 'Particle Controller Serial Number',
        var: 'controllerSerial',
        options: {
          rules: [
            {
              required: true,
              message: 'Particle Controller Serial Number is required.',
            },
          ],
        },
        props: {
          // AER-1675 (failed testing 2026-08-06, item 3): this field must NOT be
          // focused on load. Required rules validate on blur (shared <Form>,
          // AER-1667 R2), and the tech never starts here — the first click is always
          // Equipment Type — so autofocusing blurred an empty required field and
          // painted "Particle Controller Serial Number is required." before any input
          // had been given. Supersedes AER-1643, which added `autoFocus: true` here to
          // restore the pre-AntD6 `this.controllerSerial.select()`; the form now loads
          // with no field focused.
          suffix: <span />,
          onChange: (e: any) => {
            const val = e.target.value;

            const checks = [
              size(val) >= 30,
              size((val || '').split(' ')) > 1
            ]

            if (size(checks.filter(c => !!c)) >= size(checks)) { //all true, so this means its from a QR code scan, and we only want the beginning part of this value
              const realVal = (val || '').split(' ')[0];
              message.info('QR code recognized');
              setTimeout(() => {
                this.props.form.setFieldsValue({ controllerSerial: realVal });
              }, 50)
            }
          },
          onBlur: async () => {
            const {
              form: { setFields, getFieldValue },
            } = this.props;
            const str = this.getFieldKey('controllerSerial');
            const that = get(this, str);
            const eqSNKey = this.getFieldKey('equipmentSN');
            const eqSNField = get(this, eqSNKey);
            let value = getFieldValue(that.var);
            if (!value) return;

            set(that, 'props.suffix', <Icon type="loading" />);
            this.setState({ ...this.state });
            setFields([
              {
                name: 'controllerSerial',
                value,
                errors: [],
              },
            ]);
            const serial = await getSerial(value);
            const ignore = this.ignoreValidation();

            set(this, 'state.serial', serial);
            const isValid = ignore || (!!serial as unknown);
            if (isValid === -1) {
              value = getFieldValue(that.var);
              setFields([
                {
                  name: 'controllerSerial',
                  value,
                  errors: [],
                },
              ]);
              set(this, str + '.props.suffix', <span />);
            } else if (isValid) {
              value = getFieldValue(that.var);
              setFields([
                {
                  name: 'controllerSerial',
                  value,
                  errors: [],
                },
              ]);
              set(
                this,
                str + '.props.suffix',
                <Icon
                  type={'check-circle'}
                  css={css(`color: ${colors.success};`)}
                />
              );
            } else {
              value = getFieldValue(that.var);
              setFields([
                {
                  name: 'controllerSerial',
                  value,
                  errors: [
                    'Controller serial number entered can not be found. Please re-scan or re-enter controller serial number or contact an Admin.',
                  ],
                },
              ]);
              set(
                this,
                str + '.props.suffix',
                <Icon
                  type={'close-circle'}
                  css={css(`color: ${colors.error};`)}
                />
              );
            }
            this.setState({ ...this.state });
            if (getFieldValue(get(eqSNField, 'var'))) {
              blurCheck();
            }
          },
        },
      },
      {
        title: () => {
          let value = this.getEqType();
          return value == 'utility'
            ? `Equipment Serial Number`
            : value === 'handpiece' ? 'System Serial Number' : `${uppercaseFirst(value)} Serial Number`;
        },
        var: `equipmentSN`,
        // AER-1795: shared <SerialNumberInput>. The SN is checked against the selected
        // model's snTypeCodes, including the build-year window (a unit is built now).
        type: 'serialNumber',
        validateSerial: (serial: string) =>
          this.ignoreValidation()
            ? []
            : _checkEquipmentSN(
              serial,
              this.props.form.getFieldValue('modelId'),
              this.getEqType(),
              {
                models: this.state.models,
                selectableModels: this.getSelectableModels(),
              }
            ).map(e => e.message),
        onSerialValidated: afterSerialValidated,
        options: () => ({
          validateTrigger: 'onBlur',
          rules: [
            {
              required: true,
              message: `${this.getEqType() == 'utility'
                ? 'Equipment'
                : this.getEqType() === 'handpiece' ? 'System' : uppercaseFirst(this.getEqType())
                } Serial Number is required`,
            },
          ],
        }),
        props: {
          onChange: (e: any) => {
            const val = e.target.value;

            const checks = [
              size(val) >= 34,
              size((val || '').split('/')) > 1,
              val.indexOf('$+BU')
            ]

            if (size(checks.filter(c => !!c)) >= size(checks)) { //all true, so this means its from a QR code scan, and we only want the middle part of this value
              const middle = (val || '').split('/')[1];
              const realVal = middle.split('$+')[1];
              message.info('QR code recognized');
              setTimeout(() => {
                this.props.form.setFieldsValue({ equipmentSN: realVal });
              }, 50)
            }

            const checks2 = [
              size(val) >= 34,
              size((val || '').split(']')) > 1,
              val.indexOf('$]ABU')
            ]

            if (size(checks2.filter(c => !!c)) >= size(checks2)) { //all true, so this means its from a QR code scan, and we only want the middle part of this value
              const middle = (val || '').split(']')[2];
              const realVal = middle.split('&')[0];
              message.info('QR code recognized');
              setTimeout(() => {
                this.props.form.setFieldsValue({ equipmentSN: realVal });
              }, 50)
            }
          },
        },
      },
    ] as unknown) as IFormItem[];

    this.state = {
      isEdit: true,
      canEditRole: false,
      loading: false,
      loadingEquipments: false,
      equipment,
      equipments: [],
      allEquipments: [],
      serial: undefined,
      toDisplay,
      toDisplay2: [],
      modelId: '',
      models: [],
      existingEquipment: undefined,
      eqToDelete: undefined,
      tutModal: { open: false },
      platform: undefined,
    } as IState;
  }

  //scenario 4
  openEquipHasDiffControllerModal = (
    equipWithSN: Equipment[],
    controllerSerialInput: string
  ) => {
    return new Promise<void>(resolve => {
      this.props.app.modal.confirm({
        title: (
          <p css={css(`color: ${colors.error};`)}>
            {
              'Equipment is already associated with a different\ncontroller serial number'
            }
          </p>
        ),
        icon: (
          <Icon type={'exclamation-circle'} style={{ color: colors.error }} />
        ),
        content:
          'This equipment serial number already exists, and is currently associated with a different Particle serial number. Please double-check that you have entered the correct equipment serial number.',
        okText: 'Continue',
        onOk: async () => {
          const serial = await getSerial(controllerSerialInput);
          this.setState({ serial });
          resolve();
        },
      });
    });
  };
  // scenario 5
  openEquipExistsModal = (
    controllerSN: string,
    existingEquipment: Equipment
  ) => {
    return new Promise<void>(resolve => {
      this.props.app.modal.info({
        title:
          'Controller serial number is already associated with this equipment.',
        content:
          'Submitting this form will update applicable serial numbers and \
          re-run manufacturing test.',
        okText: 'Continue',
        onOk: async () => {
          const serial = await getSerial(controllerSN);
          this.setState({ serial, existingEquipment });
          resolve();
        },
      });
    });
  };

  checkControllerSerialNumber = async (
    controllerSN: string,
    equipSN = '',
    equipWithSN: Equipment[] | undefined
  ) => {
    return new Promise(async resolve => {
      const allEquipment = this.state.allEquipments;
      const canOpenModal = checkContAssocWithOtherEquip(
        controllerSN.toUpperCase(),
        equipSN,
        allEquipment
      );
      const serial = await getSerial(controllerSN.toUpperCase());

      // 0, not valid, doesn't exist
      // 1, is already associated with a piece of equip. Open modal (scenario 3)
      // 2, good to go (scenario 2)
      const option = canOpenModal ? 1 : !serial ? 0 : 2;
      const openModal = () => {
        this.props.app.modal.info({
          title:
            'Controller serial number is already associated with a different piece of equipment.',
          content:
            // tslint:disable-next-line: max-line-length
            !!size(equipWithSN)
              ? // tslint:disable-next-line: max-line-length
              `Submitting this form will update the entered equipment record with the entered Particle controller serial number. Controller serial number will be removed from any previous records.`
              : // AER-1756: equipment records are keyed on deviceId, which comes from the
              // entered controller serial - so no second record is ever created here. The
              // record already holding that controller serial is overwritten in place.
              (
                <div>
                  <p>
                    This Particle controller serial number is already assigned
                    to a different piece of equipment.
                  </p>
                  <p>
                    Submitting this form will not create a new record — it will
                    overwrite that existing equipment record with the serial
                    numbers entered here, and the previous equipment serial
                    number will no longer exist in the system.
                  </p>
                </div>
              ),
          okText: 'Continue',
          onOk: async () => {
            resolve(true);
          },
        });
      };

      this.setState({ serial, eqToDelete: equipWithSN });
      if (option === 0) {
        resolve(false);
      } else if (option === 1) {
        openModal();
      } else if (option === 2) {
        resolve(true);
      }
    });
  };

  retrieveEquipments = async (fromState = true, showLoad = true) => {
    this.clearInterval();
    try {
      this.setState({ loadingEquipments: showLoad });
      const allEquipments = await getEquipments(undefined, false, fromState);
      const eqtype = this.getEqType();

      const isType = (e: Equipment) => {
        const etype = getEqType(e);
        if (eqtype === 'utility') {
          const types = ['compressor', 'vacuum', 'unknown'];
          return types.indexOf(etype) > -1;
        } else {
          return eqtype == etype;
        }
      };

      // AER-1725: this used to write the freshness counter onto each equipment with
      // `e._testDiff = diff`. getEquipments(..., fromState) hands back the live redux
      // `dash.equipment` objects, so the counter landed on the record itself and every
      // later save from any screen carried it. It is only ever a filter test, so resolve
      // it per-item instead of storing it on the object.
      const testDiff = (e: Equipment) => {
        if (!e.test && !e.testCompletion) {
          return undefined;
        }
        //testCompletion first so that we can actually show some data on qa
        return moment().diff(moment(e.testCompletion || e.test), 'days');
      };

      const equipments = (allEquipments
        .filter(e => {
          const diff = testDiff(e);
          const passesdiff = isProd() ? (diff !== undefined && diff <= 7 ? true : false) : !!(e as any).test; //force things to showup for manu on qa

          return !!e.modelId && passesdiff && isType(e);
        })) as Equipment[];
      this.setState({
        equipments: orderBy(equipments, 'test', 'desc'),
        allEquipments,
        loadingEquipments: false,
      });
    } catch (err) {
      this.setState({
        loadingEquipments: false,
      });
    } finally {
      this.setInterval();
    }
  };

  //@ts-ignore
  interval: any = undefined;

  setInterval = () => {
    // AER-1675 (failed testing 2026-08-06, item 1): this poll passed fromState=true,
    // so every tick re-read the same cached redux `dash.equipment` array that was
    // populated once when the dashboard booted. The grid therefore could never show a
    // unit that was created after sign-in, nor pick up the testDataUrl the server
    // attaches once the results PDF is written — only a full page reload repopulated
    // the cache, which is exactly why the just-tested chair appeared solely after a
    // shift+refresh. Nothing pushes this screen over Pusher either (EquipPusher binds
    // per-device publishes into dash.equipMap, not into the equipment list), so this
    // interval is the only refresh path there is: make it hit the API.
    this.interval = setInterval(() => this.retrieveEquipments(false, false), 60 * 1000) //constantly updates this screen every 60 seconds...
  }
  clearInterval = () => {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  openChairModal = (record = {}) => {
    return new Promise<void>((resolve, reject) => {
      try {
        const radioStyle = {
          display: 'block',
          height: '30px',
          lineHeight: '30px',
        };

        let addState = {
          mount: 'chair',
          equipped: true,
        }

        // AER-1676: non-smart Forest chairs (5400/CRK) get a streamlined walkthrough.
        const selectedModelId =
          this.props.form.getFieldValue('modelId') || get(record, 'modelId', '');
        const isNonSmart = isNonSmartChair(selectedModelId);

        const generateTutModal = (obj: any = {}) => {
          const cconfig = get(obj, 'chairconfig', get(record, '_status.chair_config', {}));
          // AER-1675 / AER-1713: compare the reported firmware/hardware against the
          // model actually being manufactured (5400 / CRK / 6400). This previously
          // always resolved the 6400 record, so 5400 and CRK were checked against
          // the wrong model's approved version lists.
          const model = get(this, 'props.sState.dash.models', []).find((m: Model) => m.id === selectedModelId);

          const firmwares = [
            { val: get(cconfig, 'fw_version'), title: 'Firmware: ', prefix: 'v', comp: ensureArray(get(model, 'fw_version')) },
            { val: get(cconfig, 'data.control_board_fw'), prefix: '', title: 'CB Firmware: ', comp: ensureArray(get(model, 'control_board_fw')) },
            { val: get(cconfig, 'data.control_board_hw'), prefix: '', title: 'CB Hardware: ', comp: ensureArray(get(model, 'control_board_hw')) },
          ].map(c => {
            return {
              ...c,
              // An empty approved-version list means the model defines no constraint
              // for that board, so there is nothing to fail against. Without this the
              // PASS button below (disabled: !allValid) would be permanently disabled
              // for any chair model whose version lists have not been populated.
              isValid: !size(c.comp) || c.comp.includes(`${c.val}`),
            }
          })

          const allValid = size(firmwares.filter(f => f.isValid)) >= size(firmwares);

          const allScreens: any[] = [
                {
                  title: 'Verify Hardware & Firmware',
                  body: () => (
                    <div css={css(styles.textContainer)}>
                      <div className='text'>
                        <b>Confirm that the installed hardware and firmware is using the latest versions</b>
                        <br /><br />
                        <Button title='Get Config' onClick={getChairConfig} loading={obj.updateLoading} />
                        <br /><br />
                        {firmwares.map(v => <div><span style={!v.isValid ? { fontWeight: 'bold', color: colors.error } : {}}>{`${v.title}${v.prefix || ''}${v.val}`}</span><span>{`${!v.isValid ? ` (should be ${v.prefix || ''}${v.comp})` : ''}`}</span></div>)}
                      </div>
                    </div>
                  ),
                  buttons: [
                    {
                      title: 'FAIL',
                      type: 'primary',
                      size: 'large',
                      style: { backgroundColor: colors.error, borderColor: colors.error },
                      onClick: (p: any) => {
                        p.events.close();
                      }
                    },
                    {
                      title: 'PASS',
                      type: 'primary',
                      size: 'large',
                      disabled: !allValid,
                      style: !allValid ? {} : { backgroundColor: colors.success, borderColor: colors.success },
                      onClick: (p: any) => {
                        p.events.next();
                      }
                    },
                  ]
                },
                {
                  title: 'Calibration & Manual Inspections',
                  body: () => (
                    <div css={css(styles.textContainer)}>
                      <div className='imageContainer'><img src={'https://particle-images.s3.amazonaws.com/touchpad.png'} /></div>

                      <div className='text'>
                        <b>After calibration sequence has completed, perform the following checks:</b>
                        <br /><br />

                        <i>Manual arrow buttons</i><br /><br />
                        <ol>
                          <li>Depress and hold one of the arrow buttons.  Verify the chair responds with a single chime and moves continuously.</li>
                          <li>Verify the chair halts movement when it reaches the maximum travel for the selected direction.</li>
                          <li>Depress the same arrow button; verify chair did not move and responded with an error chime.</li>
                          <li>Repeat steps 1-3 for the remaining arrow buttons.</li>
                        </ol>

                        <i>Preset Verification (1,2,3 and A)</i><br /><br />
                        <ol>
                          <li>Depress one of the preset buttons; verify chair responds with a single chime then moves to the desired preset location and responds with a double chime when preset location is achieved.</li>
                          <li>Verify movement halts when pressing another preset or arrow button and responds with a single chime.</li>
                          <li>Repeat steps 1-2 for the remaining preset buttons.</li>
                        </ol>

                        <i>Last Position (L)</i><br /><br />
                        <ol>
                          <li>Depress ‘L’ button for Last Position; verify chair responds with a single chime then moves to the last selected preset location and responds with a double chime when last location is achieved.</li>
                        </ol>
                      </div>
                    </div>
                  ),
                  buttons: [
                    {
                      title: 'FAIL',
                      type: 'primary',
                      size: 'large',
                      style: { backgroundColor: colors.error, borderColor: colors.error },
                      onClick: (p: any) => {
                        p.events.close();
                      }
                    },
                    {
                      title: 'PASS',
                      type: 'primary',
                      size: 'large',
                      style: { backgroundColor: colors.success, borderColor: colors.success },
                      onClick: (p: any) => {
                        p.events.next();
                      }
                    },
                  ]
                },
                {
                  title: 'Chair Functionality Tests',
                  body: () => (
                    <div css={css(styles.textContainer)}>
                      <div className='imageContainer'><img src={'https://particle-images.s3.amazonaws.com/pendant.png'} /></div>

                      <div className='text'>
                        Cycle heat selection; confirm pendant LEDs illuminate as shown.
                        <br /><br />
                        Cycle massage selection and feel backrest for vibration; confirm pendant LEDs illuminate as shown.
                        <br /><br />
                        <b>If both tests pass, select PASS button below.  If either or both tests fail, select FAIL button below.</b>
                      </div>
                    </div>
                  ),
                  buttons: [
                    {
                      title: 'FAIL',
                      type: 'primary',
                      size: 'large',
                      style: { backgroundColor: colors.error, borderColor: colors.error },
                      onClick: (p: any) => {
                        p.events.close();
                      }
                    },
                    {
                      title: 'NOT EQUIPPED',
                      type: 'primary',
                      size: 'large',
                      style: { backgroundColor: colors.warning, borderColor: colors.warning },
                      onClick: (p: any) => {
                        addState.equipped = false;
                        p.events.next();
                      }
                    },
                    {
                      title: 'PASS',
                      type: 'primary',
                      size: 'large',
                      style: { backgroundColor: colors.success, borderColor: colors.success },
                      onClick: (p: any) => {
                        p.events.next();
                      }
                    },
                  ]
                },
                {
                  title: 'Seat Occupancy',
                  body: () => (
                    <div css={css(styles.textContainer)}>
                      <div className='text center'>
                        Add weight to rear area of seat cushion, then press ‘Update’ to trigger data publish.  Once occupancy is detected in data publish, PASS button will be enabled.
                        <br /><br />
                        <Button title='UPDATE' onClick={updateChair} loading={obj.updateLoading} />
                        <br />
                        <div>
                          {!!obj.canContinue && 'Occupancy detection is working as expected.  Please remove weight before proceeding.'}
                          {!obj.canContinue && <br />}
                        </div>
                      </div>
                    </div>
                  ),
                  buttons: [
                    {
                      title: 'FAIL',
                      type: 'primary',
                      size: 'large',
                      style: { backgroundColor: colors.error, borderColor: colors.error },
                      onClick: (p: any) => {
                        p.events.close();
                      }
                    },
                    {
                      title: 'PASS',
                      type: 'primary',
                      size: 'large',
                      disabled: !obj.canContinue,
                      style: !obj.canContinue ? {} : { backgroundColor: colors.success, borderColor: colors.success },
                      onClick: (p: any) => {
                        p.events.next();
                      }
                    },
                  ]
                },
                {
                  title: 'Sensor Validation',
                  body: () => (
                    <div css={css(styles.textContainer)}>
                      <div className='text center'>
                        Plug in air pressure sensor fitting to compressor, then start compressor (depressurize if needed).  Plug in vacuum sensor fitting to vacuum, start vacuum, set vacuum to 7 inHg. Verify that all sensors are connected and working as expected.
                        <br /><br />
                        <Button title='UPDATE' onClick={updateSensorChair} loading={obj.sensorLoading} />
                        <br /><br />
                        {!!obj.sensors && <div>
                          <ul style={{ marginBottom: 0, textAlign: 'left' }}>
                            {obj.sensors.map((s: any) => (<li>{s.title}: <span>{s.passed ? 'PASS' : 'FAIL'}</span></li>))}
                          </ul>
                        </div>}
                        {!obj.sensors && [0, 0, 0, 0].map(a => <br />)}
                      </div>
                    </div>
                  ),
                  buttons: [
                    {
                      title: 'FAIL',
                      type: 'primary',
                      size: 'large',
                      style: { backgroundColor: colors.error, borderColor: colors.error },
                      onClick: (p: any) => {
                        p.events.close();
                      }
                    },
                    {
                      title: 'PASS',
                      type: 'primary',
                      size: 'large',
                      disabled: !obj.sensorContinue,
                      style: !obj.sensorContinue ? {} : { backgroundColor: colors.success, borderColor: colors.success },
                      onClick: (p: any) => {
                        p.events.next();
                      }
                    },
                  ]
                },
                {
                  title: 'Chair Installation',
                  body: () => (
                    <div css={css(styles.textContainer)}>
                      <div className='text center'>
                        Select the correct configuration for how the Chair will be installed:
                        <br /><br />
                        <Radio.Group onChange={e => { addState.mount = e.target.value; generateTutModal(obj); }} value={addState.mount}>
                          <Radio style={radioStyle} value={'chair'}>
                            Chair mount
                          </Radio>
                          <Radio style={radioStyle} value={'non_chair'}>
                            Non-chair mount
                          </Radio>
                        </Radio.Group>
                      </div>
                    </div>
                  ),
                  buttons: [
                    {
                      title: 'SUBMIT',
                      type: 'primary',
                      size: 'large',
                      disabled: !obj.sensorContinue,
                      style: !obj.sensorContinue ? {} : { backgroundColor: colors.success, borderColor: colors.success },
                      onClick: (p: any) => {
                        this.setState({ tutModal: { open: false } });
                        resolve({ ...p, ...obj, ...addState })
                      }
                    },
                  ]
                },
          ];

          // AER-1676: non-smart Forest chairs (5400/CRK) run a streamlined 2-screen
          // walkthrough (Verify Hardware & Firmware + Calibration & Manual
          // Inspections) with no sensor validation; the final PASS submits the result.
          let screens = allScreens;
          if (isNonSmart) {
            screens = allScreens.slice(0, 2);
            const last = screens[screens.length - 1];
            last.buttons = last.buttons.map((b: any) =>
              b.title !== 'PASS'
                ? b
                : {
                    ...b,
                    title: 'SUBMIT',
                    onClick: (p: any) => {
                      this.setState({ tutModal: { open: false } });
                      resolve({ ...p, ...obj, ...addState });
                    },
                  }
            );
          }

          this.setState({
            tutModal: {
              open: true,
              onClose: (tutstate: {}) => {
                this.setState({ tutModal: { open: false } });
                reject({ ...tutstate, sensors: getSensors(), fail: true });
              },
              screens,
            },
          });
        }

        let latestStat: any = {};

        const getSensors = (stat = latestStat) => {
          const sensors = [
            { title: `Seat Sensor`, passed: !get(stat, 'in_chair'), val: "" },
            { title: `Ambient Temperature`, passed: !!get(stat, 'AT'), val: get(stat, 'AT', null), suffix: " °C" },
            { title: `Ambient Humidity`, passed: !!get(stat, 'AH'), val: get(stat, 'AH', null), suffix: "%" },
            { title: `Pressure`, passed: isBetween(get(stat, 'LP') || 0, [53, 67]), val: get(stat, 'LP', null), suffix: " PSI" },
            { title: `Vacuum`, passed: isBetween(get(stat, 'LV') || 0, [6.2, 7.8]), val: get(stat, 'LV', null), suffix: " inHg" },
          ]

          return sensors;
        }

        const deviceId: any = get(this, 'state.serial.deviceId');

        const getChairConfig = async () => {
          try {
            generateTutModal({ updateLoading: true, canContinue: false });

            latestStat = await checkForNewStatus({ deviceId } as Equipment, {
              key: 'chair_config.published_at',
              hasOriginal: () => {
                sendDeviceCommand(deviceId, { arg: 'get_config' }, { showErrorToast: false }).catch(e => 0)
              },
            }) as unknown as {};

            const chairconfig = get(latestStat, '_status.chair_config', {});

            console.log({
              latestStat,
            })

            generateTutModal({ updateLoading: false, chairconfig })
          } catch (err) {
            message.error(`Unable to communicate with equipment`);
            generateTutModal({ updateLoading: false, canContinue: false })
          }
        }

        const updateChair = async () => {
          try {
            generateTutModal({ updateLoading: true, canContinue: false });

            latestStat = await checkForNewStatus({ deviceId } as Equipment, {
              key: 'chair_sensor_instant.published_at',
              hasOriginal: () => {
                sendDeviceCommand(deviceId, { arg: 'get_data' }, { showErrorToast: false }).catch(e => 0)
              },
            }) as unknown as {};

            const val = get(latestStat, '_status.chair_sensor_instant.data.in_chair', false);

            console.log({
              latestStat,
            })

            generateTutModal({ updateLoading: false, canContinue: val })
          } catch (err) {
            message.error(`Unable to communicate with equipment`);
            generateTutModal({ updateLoading: false, canContinue: false })
          }
        }

        const updateSensorChair = async () => {
          try {
            generateTutModal({ sensorLoading: true, sensorContinue: false });

            const key = 'chair_sensor_instant';

            latestStat = get(await checkForNewStatus({ deviceId } as Equipment, {
              key: `${key}.published_at`,
              hasOriginal: () => {
                sendDeviceCommand(deviceId, { arg: 'get_data' }, { showErrorToast: false }).catch(e => 0)
              },
            }), `_status.${key}.data`);

            const sensors = getSensors();

            console.log({
              stat: latestStat,
              sensors,
            })

            const allpassed = sensors.filter(s => s.passed).length >= sensors.length;

            generateTutModal({ sensorLoading: false, hasStats: true, sensors, sensorContinue: allpassed })
          } catch (err) {
            message.error(`Unable to communicate with equipment`);
            generateTutModal({ sensorLoading: false, sensorContinue: false });
          }
        }

        generateTutModal();
      } catch (err) {
        console.error(err);
        reject(err);
      }
    })
  }

  showCommandLineModal = (e: Equipment) => {
    if (!e.deviceId) {
      console.log('Attempted to show command line modal for equipment with blank device ID');
      return;
    }

    const header = 'Command Line to Flash Firmware';
    const commandLine = `particle cloud flash --product 9783 ${e.deviceId} cloud-disable-fw-utility.bin`;

    const copyCommand = () => {
      navigator.clipboard.writeText(commandLine);
      message.success("Command line copied to clipboard");
    };

    Modal.info({
      title: header,
      content: <div>
        <div>This command line can be used to manually flash the firmware to a piece of equipment.</div>
        <div><span style={{fontWeight: 'bold'}}>WARNING: </span>This should only be done on confirmed non-connected units. This action cannot be undone.</div>
        <div style={{fontFamily: "monospace", wordWrap: "break-word"}}>{commandLine}<Button outline style={{marginLeft: '5px', }} icon='copy' onClick={copyCommand}/></div>
      </div>,
      okText: 'Close',
      style: {
        width: '80%',
      },
    });
  };

  submit = debounce(
    async (e: MouseEvent | FormEvent) => {
      e.preventDefault();
      if (this.state.loading) {
        return;
      }

      const state = { ...this.state };

      state.loading = true;
      this.setState(state);

      const form = this.props.form;
      const { getFieldError } = form;

      const cSerialError = getFieldError('equipmentSN');
      if (size(cSerialError) > 0) {
        message.error(cSerialError.join(' '));
        return this.setState({
          loading: false,
        });
      }

      form.validateFieldsAndScroll(async (err: any, values: any) => {
        if (err) {
          message.error(buildErrorMsgFromForm(err));
          return this.setState({
            loading: false,
          });
        }

        try {
          const openConfirmModal = () => {
            const openerText =
              'Please review and confirm that the correct model has been selected and that all \
            serial numbers scanned or entered for Particle controller and equipment parts are \
            accurate. If any data is not accurate, close this confirmation and update the data.';

            const genFieldsText = () => {
              const { toDisplay, toDisplay2 } = this.state;
              let text: ReactElement[] = [];
              [...toDisplay, ...toDisplay2].map(td => {
                const canDisplay = td.canDisplay ? td.canDisplay() : true;
                if (canDisplay) {
                  const val = get(values, td.var, false);
                  const title =
                    typeof td.title === 'function' ? td.title() : td.title;
                  text.push(
                    <li>
                      <span css={css('font-weight: bold')}>{`${title}: `}</span>
                      {`${val}`}
                    </li>
                  );
                }
              });
              return text;
            };

            this.props.app.modal.confirm({
              title: 'Confirm Equipment Details',
              width: '80%',
              content: (
                <div>
                  <p>{openerText}</p>
                  <ul>{genFieldsText().map(t => t)}</ul>
                </div>
              ),
              okText: 'Start Test',
              onCancel: () => {
                this.setState({ loading: false });
              },
              onOk: () => {
                this.startTest(values);
              },
            });
          };
          openConfirmModal();
        } catch (err) {
          message.error('Save failed: ' + err.message);
          return this.setState({
            loading: false,
          });
        }
      });
    },
    500,
    { leading: false }
  );

  ignoreValidation = () => {
    const {
      form: { getFieldValue },
    } = this.props;
    return isPrototypeSerial(getFieldValue('equipmentSN'));
  };

  // tslint:disable-next-line: no-any
  startTest = async (values: any) => {
    try {
      const { sState } = this.props;
      const {
        toDisplay,
        toDisplay2,
        existingEquipment,
        eqToDelete,
      } = this.state;
      const me: any = get(sState, 'auth.user');
      const eq = new _Equipment(existingEquipment);
      let mid = get(values, 'modelId');
      if (!!get(values, 'include_ss')) {
        mid += 'SS';
      }
      const models = get(sState, 'dash.models', []) as Model[];
      const model = models.find(m => m.id === mid) as Model;
      const modelType = get(model, 'type') as string;
      const serial = get(values, 'controllerSerial').toUpperCase();

      const eqType = this.getEqType();
      // AER-1676: non-smart Forest chairs (5400/CRK) run a streamlined process.
      const isNonSmart = isNonSmartChair(get(values, 'modelId'));

      [...toDisplay, ...toDisplay2].map(td => {
        const canDisplay = td.canDisplay ? td.canDisplay() : true;
        if (canDisplay) {
          const val = get(values, td.var);
          set(eq, td.var, val);
        }
      });

      set(eq, 'modelId', mid);

      const equipmentSN = get(eq, 'equipmentSN', '')
        .toUpperCase()
        .trim();
      const mSerial1 = get(eq, 'mSerial1', '')
        .toUpperCase()
        .trim();
      const mSerial2 = get(eq, 'mSerial2', '')
        .toUpperCase()
        .trim();
      const mSerial3 = get(eq, 'mSerial3', '')
        .toUpperCase()
        .trim();
      const tankSerial = get(eq, 'tankSerial', '')
        .toUpperCase()
        .trim();
      const dryerSerial = get(eq, 'dryerSerial', '')
        .toUpperCase()
        .trim();
      const pumpSerial = get(eq, 'pumpSerial', '')
        .toUpperCase()
        .trim();

      set(eq, 'equipmentSN', equipmentSN);
      set(eq, 'controllerSerial', serial.trim());
      set(eq, 'name', equipmentSN);
      set(eq, 'tankSerial', tankSerial);
      set(eq, 'dryerSerial', dryerSerial);
      set(eq, 'pumpSerial', pumpSerial);

      if (model && model.heads !== undefined) {
        if (model.heads > 0) {
          set(eq, 'mSerial1', mSerial1);
        } else {
          set(eq, 'mSerial1', '');
        }

        if (model.heads > 1) {
          set(eq, 'mSerial2', mSerial2);
        } else {
          set(eq, 'mSerial2', '');
        }

        if (model.heads > 2) {
          set(eq, 'mSerial3', mSerial3);
        } else {
          set(eq, 'mSerial3', '');
        }
      }

      const deviceId: any = get(this, 'state.serial.deviceId');

      if (!deviceId) {
        throw new Error('No device ID supplied');
      } else {
        const date = new Date().toISOString();
        set(eq, 'deviceId', deviceId);
        set(eq, 'createdBy', me.userId);
        set(eq, 'createdAt', date);
        set(eq, 'updatedAt', date);
        set(eq, 'updatedBy', me.userId);
        set(eq, 'test', date);
        set(eq, 'testStartedBy', `${me.firstName} ${me.lastName}`);
        set(eq, 'testCompletion', null);
        set(eq, 'testDataUrl', null);
        set(eq, 'showOnDash', false);
        if (eq.serialNumber) {
          delete eq.serialNumber;
        }

        await createOrUpdateEquipment(eq).catch(err => {
          Bugsnag.notify(err);
          throw new Error(
            `Unable to create Equipment record.  Please try again, and contact Technical Support if the issue persists.`
          );
        });

        const retrieveHWVer = (deviceId: string) => {
          return new Promise(async (resolve, reject) => {
            const config: IAppConfig = CONFIG(process.env.ENV_STAGE as ENVS);
            const access_token = config.particle_key;
            const url = `https://api.particle.io/v1/products/9783/devices/${deviceId}/HW_VER?access_token=${access_token}`;
            const options = {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
            };
            try {
              const response = await axios.get(url, options);
              resolve(response.data);
            } catch (error) {
              reject(error);
            }
          });
        };

        // we want to try and get/set hw_version before the test begins
        const validHwRetrieve = ['utility'].includes(eqType); //this HW_VER command is only valid with utility eq
        if (!!deviceId && validHwRetrieve && eq.hw_version === undefined) {
          try {
            const response = await retrieveHWVer(deviceId);
            const hw_version = get(response, 'result');
            !!hw_version &&
              set(eq, 'hw_version', hw_version) &&
              (await createOrUpdateEquipment(eq));
          } catch (error) {
            console.warn(`problem updating equipment hw_ver: ${error}`);
          }
        }

        const testCommand = generateTestCommand(eq, eqType);

        const sendCommands = ['utility', 'handpiece'].includes(eqType);

        if (sendCommands) {
          try {
            await sendDeviceCommand(
              deviceId,
              {
                arg: testCommand,
              },
              { showErrorToast: true },
              isHandpiece(eq)
            );
          } catch (e) {
            // @ts-ignore
            if (e.message === 'ERROR_FUNC_NAME_UNKNOWN') {
              // Specific error indicates missing function. Missing test commands strongly suggests
              // that the device is not running a manufacturing build.
              throw { message: 'Error sending command to device - please check that the device is running a manufacturing build.'};
            }
            throw e;
          }

          if (get(values, 'lockPresets') && this.getEqType() === 'handpiece') {
            await sendDeviceCommand(
              deviceId,
              {
                arg: `{"cmd":"set_cfg", "cfg": {"config":{"dental_school": true}}}`,
              },
              { showErrorToast: true },
              true
            );
          }

          if (eqToDelete && !!size(eqToDelete)) {
            await Promise.all(eqToDelete.map(deleteEquipment));
          }
        } else if (eqType === 'chair') {
          const sendCalibrate = true;

          if (sendCalibrate) {
            const resetFactory = await sendDeviceCommand(deviceId, { arg: "reset_factory" }, { showErrorToast: false }).catch(e => {
              return null;
            });

            if (!resetFactory) {
              throw new Error('Unable to reset Equipment');
            }

            const autocal = await sendDeviceCommand(deviceId, { arg: "chair_autocal" }, { showErrorToast: false }).catch(e => {
              return null;
            });

            if (!autocal) {
              throw new Error('Unable to calibrate Equipment');
            }

            // AER-1676: non-smart chairs (5400/CRK) have no sensors, so skip sensor_cal.
            if (!isNonSmart) {
              const sensorcal = await sendDeviceCommand(deviceId, { arg: "sensor_cal" }, { showErrorToast: false }).catch(e => {
                return null;
              });

              if (!sensorcal) {
                throw new Error('Unable to calibrate Equipment');
              }
            }
          }

          const record = await getEquipmentRecord(deviceId, true);

          const chairData = await this.openChairModal(record).catch(e => e);

          if (chairData.mount === 'non_chair') {
            const chairmountcommand = await sendDeviceCommand(deviceId, { arg: "chair_mount,0" }, { showErrorToast: false }).catch(e => {
              return null;
            });

            if (!chairmountcommand) {
              throw new Error('Unable to calibrate Equipment');
            }
          }

          if (chairData.fail) {
            // AER-1804: status is written by the server only; it stores the failed result together
            // with the completion, so the grid cannot show a completed test without its result
            await recordFailedTest(deviceId);
            throw new Error('Test Failed')
          } else {
            const pubSend = {
              "data": {
                "overall": 1,
                ...chairData,
              },
              "public": false,
              "coreid": deviceId,
              "event": "dz_testresults",
              "published_at": new Date().toISOString(),
              "userid": "manufacturing",
              "fw_version": get(record, 'fw_version', 0)
            };

            // AER-1675: deliberately not awaited — the server only responds once the
            // test-results PDF has finished generating. Previously any failure here
            // was dropped silently, so a run that produced no test results looked
            // identical to a successful one; surface it instead.
            createPublish(pubSend).catch((e: Error) => {
              Bugsnag.notify(e);
              message.error(
                'Test results could not be generated. Please contact Technical Support.'
              );
            });

            message.success(
              `Manufacturing test started for ${uppercaseFirst(
                modelType
              )} ${serial}`
            );

            try {
              await sendDeviceCommand(deviceId, { arg: "mfg_complete" }, { errorMessage: 'Failed to initialize install date tracking.'});
            } catch (e) {
              console.log('Failed to send mfg_complete command: ' + JSON.stringify(e));
            }
          }
        }

        // AER-1676: skip Particle-name update for non-smart chairs (5400/CRK) — the
        // reused board is not permanently assigned and the record is auto-deleted.
        if (!isNonSmart) {
          await updateParticleName({ deviceId, equipment: eq }).catch(() => 0)
        }

        this.setState(
          {
            loading: false,
          },
          async () => {
            await this.clear();
            // AER-1675: refresh from the API, not from redux. The record was created
            // seconds ago by createOrUpdateEquipment and is not in the cached
            // `dash.equipment` array, so a fromState refresh left the grid unchanged
            // until the tech force-reloaded the page.
            this.retrieveEquipments(false);
          }
        );
      }
    } catch (err) {
      message.error(err.message);
      this.setState({ loading: false }, () => this.retrieveEquipments(false));
    }
  };

  retrieveModels = async () => {
    try {
      const models = await getModels();
      this.setState({ models: models.filter(m => !!get(m, 'isActive', 1)) });
    } catch (err) {
      console.error(err);
    }
  };

  componentDidMount = () => {
    this.retrieveEquipments();
    this.retrieveModels();
    this.setFieldsOriginal();
  };
  componentWillUnmount = () => {
    this.clearInterval();
  }
  setFieldsOriginal = (flds = {}) => {
    const { toDisplay, equipment } = this.state;

    const {
      form: { setFieldsValue },
    } = this.props;

    const fields = {
      ...flds,
    };

    toDisplay.map(item => {
      let val = get(equipment, item.var, get(fields, item.var, get(item, 'props.initialValue')));

      if (item.match) {
        val = get(this, item.match, val);
      }

      set(
        fields,
        item.var,
        item.type === 'datepicker'
          ? val && (moment(val as string) as unknown)
          : val
      );
    });

    setFieldsValue(fields);

    // AER-1643: under the AntD6 migration the shared <Form> keeps input refs in
    // its own internal store (refs.current[var]) and no longer assigns them onto
    // this component, so `this.controllerSerial` is always undefined. The
    // unguarded `.select()` threw in componentDidMount → white screen for
    // manufacturing (role 5) users on sign-in. Guard the call.
    // AER-1675: focus-on-load is no longer wanted at all (see the controllerSerial
    // field above), so this stays a no-op guard rather than being re-wired.
    const comp: any = get(this, 'controllerSerial');
    if (comp && typeof comp.select === 'function') {
      comp.select();
    }
  };
  getFieldKey = (key: string, sel = `state.toDisplay`) => {
    const index = (get(this, sel, []) as any[]).findIndex(
      (f: { var: string }) => f.var == key
    );
    return `${sel}.${index}`;
  };
  getEqType = () => {
    const {
      form: { getFieldValue },
    } = this.props;
    return getFieldValue('eqType') || 'utility';
  };
  // What the Equipment Model dropdown offers: models shown on Manufacturing, for the
  // selected equipment type (every type while a prototype SN is entered).
  getSelectableModels = (allTypes = false): Model[] => {
    const eqType = this.getEqType();
    return (get(this, 'state.models', []) as Model[])
      .filter(m => get(m, 'showOnManu', true))
      .filter(m => {
        if (allTypes) {
          return true;
        }
        if (eqType === 'handpiece') {
          return m.type === 'handpiece';
        } else if (eqType === 'chair') {
          return m.type === 'chair';
        }
        // AER-1653: Utility must never list chair (e.g. 6400) or handpiece
        // (e.g. E900) models. AER-1683: the model dropdown now leads the form
        // and no longer narrows by equipment-SN prefix — SN↔model matching is
        // enforced by _checkEquipmentSN on the SN field instead.
        return m.type !== 'handpiece' && m.type !== 'chair';
      });
  };
  clear = () => {
    return new Promise((resolve) => {
      const { toDisplay, toDisplay2 } = this.state;
      // AER-1657: the dynamic "bottom" fields (motor heads, tank/dryer serial, etc.)
      // live in toDisplay2. Emptying that array only un-renders them — their values
      // linger in the AntD form store (preserved while unmounted) and reappear when
      // the same equipment type is reselected. Capture their vars so we can blank
      // them in the form below.
      const dynamicVars = (toDisplay2 || []).map((td: any) => td.var);
      const state = { ...this.state };
      // AER-1657: reset the controller serial's ✓/✗ check icon. (The equipment SN's icon
      // is owned by <SerialNumberInput> and clears itself when the value is reset.)
      const ctrlIdx = toDisplay.findIndex((f: any) => f.var === 'controllerSerial');
      if (ctrlIdx > -1) set(state, `toDisplay.${ctrlIdx}.props.suffix`, <span />);
      state.toDisplay2 = [];
      state.existingEquipment = undefined;
      state.serial = undefined;
      state.equipment = new _Equipment();
      state.eqToDelete = undefined;
      const eqType = this.props.form.getFieldValue('eqType');
      // AER-1657: explicitly blank the dynamic field values so they don't persist
      // (setFieldsOriginal only re-seeds toDisplay, never toDisplay2).
      if (dynamicVars.length) {
        const cleared = dynamicVars.reduce(
          (acc: any, v: string) => { acc[v] = undefined; return acc; },
          {} as any
        );
        this.props.form.setFieldsValue(cleared);
      }
      this.setState(state, () => {
        setTimeout(() => {
          this.setFieldsOriginal({ eqType });
          setTimeout(resolve, 50);
        }, 50)
      });
    })
  };
  deleteEquip = async (e: Equipment) => {
    await deleteEquipment(e);
    this.retrieveEquipments();
  };
  // AER-1657: keep the form reactive on every value change. The shared <Form>
  // calls this with (allValues, changedValues).
  // (1) When the tech edits the controller serial, drop its ✓/✗ icon (and
  //     invalidate the controller check) so a stale check mark never lingers
  //     over freshly-changed input. <SerialNumberInput> does the same for the
  //     equipment SN on its own.
  // (2) Force a re-render so the dependent fields (canDisplay) and the ✓/✗
  //     suffixes re-evaluate live on each keystroke.
  handleValuesChange = (_allValues: any, changedValues: any) => {
    const changed = changedValues || {};
    if (Object.prototype.hasOwnProperty.call(changed, 'controllerSerial')) {
      const k = this.getFieldKey('controllerSerial');
      set(this, k + '.props.suffix', <span />);
      set(this, 'state.serial', undefined);
    }
    this.setState({});
  };
  // AER-1667 (R1): the Start Manufacturing Test button is no longer gated by
  // field validation / serial-check / outstanding errors — it stays enabled
  // except while a test is starting (`disabled={loading}` below). Submit-time
  // validation (validateFieldsAndScroll + the equipmentSN getFieldError guard)
  // and the deviceId check in startTest() still surface any problems on click.
  render() {
    const {
      form: { Form },
    } = this.props;
    const {
      toDisplay,
      toDisplay2,
      loading,
      loadingEquipments,
      equipments,
    } = this.state;

    return (
      <div css={css(styles.container)}>
        <FormTitle size={'20px'} title={`Manufacturing Test`} />

        <div css={css(styles.container)}>
          <div css={css(styles.formContainer)}>
            <Form
              layout="vertical"
              items={[...toDisplay, ...toDisplay2]}
              onValuesChange={this.handleValuesChange}
            >
              <Button
                title={'Clear'}
                disabled={loading}
                css={css`
                  margin-right: 10px;
                `}
                outline={true}
                onClick={this.clear}
              />
              <Button
                title={'Start Manufacturing Test'}
                loading={loading}
                disabled={loading}
                onClick={this.submit}
              />
            </Form>
          </div>
        </div>

        <Tutorial {...this.state.tutModal} />

        <div css={css(styles.container)}>
          <FormTitle size={'20px'} title={`Recently Tested Equipment`} />
          <TableList
            canSelectRows={false}
            columns={generateEquipColumns(this)}
            loading={loadingEquipments}
            data={equipments as never[]}
            implementScroll={false}
          />
        </div>
      </div>
    );
  }
}

export const ManufactureProcess = WithAppConfig(WithForm(withRouter(_ManufactureProcess)));
